import React, { useState, useEffect } from 'react';
import { createMilestone, updateMilestone } from '../api/milestones';
import { getProjects } from '../api/projects';
import { Milestone, MilestoneCreate } from '../types/milestone';
import { Project } from '../types/project';
import Button from './Button';

interface MilestoneFormProps {
  milestone?: Milestone;
  onSuccess?: (milestone: Milestone) => void;
  onCancel?: () => void;
}

const MilestoneForm: React.FC<MilestoneFormProps> = ({ milestone, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    target_date: '',
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = !!milestone;

  // Calculate character count for description (max 500)
  const descriptionLength = formData.description.length;
  const descriptionMaxLength = 500;

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (milestone) {
      setFormData({
        name: milestone.name,
        description: milestone.description || '',
        project_id: milestone.project_id,
        target_date: milestone.target_date,
      });
    }
  }, [milestone]);

  const loadProjects = async () => {
    try {
      const allProjects = await getProjects();
      setProjects(allProjects);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Enforce max length for description
    if (name === 'description' && value.length > descriptionMaxLength) {
      return;
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (!formData.name.trim()) {
        throw new Error('Milestone name is required');
      }
      if (!formData.project_id) {
        throw new Error('Project is required');
      }
      if (!formData.target_date) {
        throw new Error('Target date is required');
      }

      // Prepare data for API
      const milestoneData: MilestoneCreate = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        project_id: formData.project_id,
        target_date: formData.target_date,
      };

      let result: Milestone;
      if (isEditMode && milestone) {
        result = await updateMilestone(milestone.id, milestoneData);
      } else {
        result = await createMilestone(milestoneData);
      }

      if (onSuccess) {
        onSuccess(result);
      }

      // Reset form if creating new milestone
      if (!isEditMode) {
        setFormData({
          name: '',
          description: '',
          project_id: '',
          target_date: '',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save milestone';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100" role="alert">
          {error}
        </div>
      )}

      <div className="mb-6">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          required
          placeholder="e.g., Phase 1 Complete"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
          <span className="text-muted ml-2">
            ({descriptionLength}/{descriptionMaxLength} characters)
          </span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-vertical min-h-[100px]"
          rows={4}
          placeholder="Describe what this milestone represents..."
          maxLength={descriptionMaxLength}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="project_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Project <span className="text-danger">*</span>
        </label>
        <select
          id="project_id"
          name="project_id"
          value={formData.project_id}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          required
        >
          <option value="">Select a project</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label htmlFor="target_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Target Date <span className="text-danger">*</span>
        </label>
        <input
          type="date"
          id="target_date"
          name="target_date"
          value={formData.target_date}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          required
        />
        <small className="text-muted">
          Cannot be more than 10 years in the past
        </small>
      </div>

      <div className="form-actions">
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Saving...' : isEditMode ? 'Update Milestone' : 'Create Milestone'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default MilestoneForm;
